export interface ModuleDependencyForOrderLike {
  depends_on_module_id: string
}

export interface ModuleForOrderLike {
  id: string
  display_order: number
  dependencies: ModuleDependencyForOrderLike[]
}

function compareModuleIds(
  leftId: string,
  rightId: string,
  moduleById: Map<string, ModuleForOrderLike>,
  originalIndexById: Map<string, number>
) {
  const leftModule = moduleById.get(leftId)
  const rightModule = moduleById.get(rightId)
  const leftOrder = leftModule?.display_order ?? Number.MAX_SAFE_INTEGER
  const rightOrder = rightModule?.display_order ?? Number.MAX_SAFE_INTEGER
  if (leftOrder !== rightOrder) return leftOrder - rightOrder

  const leftIndex = originalIndexById.get(leftId) ?? Number.MAX_SAFE_INTEGER
  const rightIndex = originalIndexById.get(rightId) ?? Number.MAX_SAFE_INTEGER
  return leftIndex - rightIndex
}

export function sortModulesByDependencyOrder<T extends ModuleForOrderLike>(modules: T[]): T[] {
  const moduleById = new Map<string, T>()
  const originalIndexById = new Map<string, number>()
  for (const [index, module] of modules.entries()) {
    moduleById.set(module.id, module)
    originalIndexById.set(module.id, index)
  }

  const indegree = new Map<string, number>()
  const outgoing = new Map<string, string[]>()
  for (const module of modules) {
    indegree.set(module.id, 0)
    outgoing.set(module.id, [])
  }

  for (const module of modules) {
    const uniqueDependencies = new Set(module.dependencies.map((dependency) => dependency.depends_on_module_id))
    for (const dependencyId of uniqueDependencies) {
      if (!moduleById.has(dependencyId)) continue
      indegree.set(module.id, (indegree.get(module.id) ?? 0) + 1)
      outgoing.get(dependencyId)?.push(module.id)
    }
  }

  const queue = modules
    .map((module) => module.id)
    .filter((moduleId) => (indegree.get(moduleId) ?? 0) === 0)
    .sort((leftId, rightId) => compareModuleIds(leftId, rightId, moduleById, originalIndexById))

  const orderedIds: string[] = []

  while (queue.length > 0) {
    const currentId = queue.shift()
    if (!currentId) break
    orderedIds.push(currentId)

    for (const dependentId of outgoing.get(currentId) ?? []) {
      const nextIndegree = (indegree.get(dependentId) ?? 0) - 1
      indegree.set(dependentId, nextIndegree)
      if (nextIndegree === 0) {
        queue.push(dependentId)
      }
    }

    queue.sort((leftId, rightId) => compareModuleIds(leftId, rightId, moduleById, originalIndexById))
  }

  if (orderedIds.length < modules.length) {
    const missingIds = modules
      .map((module) => module.id)
      .filter((moduleId) => !orderedIds.includes(moduleId))
      .sort((leftId, rightId) => compareModuleIds(leftId, rightId, moduleById, originalIndexById))
    orderedIds.push(...missingIds)
  }

  return orderedIds.map((moduleId) => moduleById.get(moduleId)).filter((module): module is T => module !== undefined)
}
